import { useEffect, useRef } from "react";

/**
 * Avvisa l'utente prima di uscire da una schermata con modifiche non salvate.
 *
 * Copre 3 scenari tipici di Android Chrome + PWA installata:
 * 1) Tasto "<" del browser / gesto indietro → mostra confirm dialog
 * 2) Chiusura tab / refresh → mostra il prompt nativo del browser
 * 3) Navigazione interna SPA → (uso opzionale, vedi sotto)
 *
 * Pattern "sentinel history entry": pushiamo un'entry duplicata nel history
 * appena `isDirty` diventa true. In questo modo la PRIMA pressione del back
 * fa scattare il popstate sulla nostra sentinella (non sull'entry reale del
 * router), permettendoci di intercettare e chiedere conferma. Se l'utente
 * conferma, lasciamo passare la seconda pressione.
 *
 * @param {boolean} isDirty - true se ci sono modifiche da salvare
 * @param {string} [message] - testo del dialog di conferma
 */
export function useUnsavedChangesGuard(isDirty, message = "Hai modifiche non salvate. Vuoi davvero uscire? Le modifiche verranno perse.") {
  // Riferimento per evitare di gestire popstate dopo che l'utente ha confermato l'uscita
  const userConfirmedExitRef = useRef(false);

  useEffect(() => {
    if (!isDirty) return;
    userConfirmedExitRef.current = false;

    // 1) Avviso nativo su chiusura tab / refresh
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      // Per compatibilità cross-browser (Chrome richiede returnValue)
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    // 2) Sentinel history entry per intercettare il tasto indietro
    // Pushiamo una nuova entry uguale alla URL corrente. Quando l'utente preme
    // "<", il browser chiama popstate e noi blocchiamo / chiediamo conferma.
    window.history.pushState({ __unsavedGuard: true }, "");

    const handlePopState = (_e) => {
      if (userConfirmedExitRef.current) return;
      const ok = window.confirm(message);
      if (ok) {
        userConfirmedExitRef.current = true;
        // L'utente vuole davvero uscire: il pop ha già consumato la sentinel,
        // ora simuliamo un secondo "back" per andare alla pagina precedente.
        window.history.back();
      } else {
        // L'utente vuole restare: ri-pushiamo la sentinel per coprire il prossimo back
        window.history.pushState({ __unsavedGuard: true }, "");
      }
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isDirty, message]);
}
